import React, { useMemo } from "react";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { IFolder } from "store/types";

function buildFolderPath(folders: IFolder[], folderId: number | null): IFolder[] {
  if (!folderId || !folders?.length) return [];
  const folderMap = new Map(folders.map((f) => [f.id, f]));
  const path: IFolder[] = [];
  let current = folderMap.get(folderId);
  while (current) {
    path.unshift(current);
    current = current.parent_id ? folderMap.get(current.parent_id) : undefined;
  }
  return path;
}

interface Props {
  folders: IFolder[];
  folderId: number | null;
  foldersLoading: boolean;
  disabled?: boolean;
  action?: () => Promise<void>;
}

export const FolderPathDisplay: React.FC<Props> = ({ folders, folderId, foldersLoading, disabled, action }) => {
  const path = useMemo(() => buildFolderPath(folders, folderId), [folders, folderId]);

  return (
    <Stack
      sx={{
        px: "10px",
        flexDirection: "row",
        alignItems: "center",
        gap: "6px",
        ...(disabled && { opacity: 0.4, pointerEvents: "none" })
      }}
    >
      <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#496682" }}>Folder:</Typography>
      <Tooltip
        title={!foldersLoading ? `Workspace${path.map((f) => ` › ${f.title}`).join("")}` : ""}
        disableHoverListener={foldersLoading || disabled}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ maxWidth: 400, minWidth: 0, overflow: "hidden" }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "#496682",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            Workspace{path.map((f) => ` › ${f.title}`).join("")}
          </Typography>
        </Stack>
      </Tooltip>
      {action && (
        <IconButton size="sm" onClick={action}>
          <EditOutlinedIcon />
        </IconButton>
      )}
    </Stack>
  );
};
